import React, { Fragment } from 'react'

import cx from 'classnames'

import {
  A,
  Button,
  Div,
  Form,
  H1,
  Input,
  Strong,
  ToolTip,
} from '@driftt/tide-core'

import Consent from 'components/Consent'
import { DynamicNotification } from 'components/DynamicNotification'
import EmailInput from 'components/EmailInput'
import PasswordInput from 'components/PasswordInput'
import GoogleAuth from 'layouts/SignupChoiceCard/GoogleAuth'
import MicrosoftAuth from 'layouts/SignupChoiceCard/MicrosoftAuth'
import * as passUtils from 'utils/password'
import { track } from 'utils/trackingUtils'

import driftVideoSrc from '../../images/drift-video-logo-new.png'

const trackVideoSignInNotification = () => {
  track('Onboarding Setup Sign-In Triggered', {
    context: 'verify email step',
    product: 'video',
  })
}

export const INVALID_DOMAINS = [
  '@gmail.',
  '@yahoo.',
  '@hotmail.',
  '@live.',
  '@aol.',
  '@outlook.',
  '@comcast.',
  '@inbox.',
  '@icloud.',
  '@mail.com',
]

const SignupCard = ({
  name,
  email,
  password,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onContinueClick,
  onVideoSignInClick,
  disabledEmail,
  validEmail,
  isGdprConsentRequired,
  isGdprConsentGranted,
  setGdprConsentGranted,
  createOrgLoading,
  checkEmailIsSSOEnabledPending,
  splitLoading,
  hideError,
  createOrgError,
  onProcessingGoogleCode,
  onProcessingExternalGoogleCode,
  onProcessingMicrosoftCode,
  oauthError,
  onVideoCreateNewAccount,
  loginUrl,
  solvedCaptcha,
}) => {
  const displayToolTip = INVALID_DOMAINS.some((domain) =>
    email.includes(domain)
  )
  const showVideoSignInNotice =
    createOrgError && createOrgError.endsWith('has already been used')

  return (
    <Form className="video-signup-card" onSubmit={onContinueClick}>
      <Div className="video-signup-card-content">
        <img
          className="video-logo-image"
          src={driftVideoSrc}
          alt="Drift Video"
        />
        <H1 className="video-signup-header">
          <Strong>Create your Drift Account</Strong>&nbsp;to easily record and
          share videos
        </H1>
        <Input
          value={name}
          onChange={onNameChange}
          className="video-signup-input"
          placeholder="Name"
          autoFocus
        />
        <ToolTip
          className="touchless-start-tooltip"
          open={displayToolTip}
          hover={displayToolTip}
          flow="right"
          render={() =>
            'We recommend using a work email in order to get the most out of Drift'
          }
        />
        <EmailInput
          placeholder="Work email"
          className={cx('video-signup-input', {
            'touchless-email-input-error':
              !hideError &&
              !showVideoSignInNotice &&
              (!!createOrgError || !validEmail),
            'touchless-email-input-disable': disabledEmail,
          })}
          onChange={onEmailChange}
          value={email}
          hideError={hideError || showVideoSignInNotice}
          createOrgError={createOrgError}
          valid={validEmail}
          unique={!createOrgError}
          disabled={disabledEmail}
          type={'text'}
          shouldAutofocus={false}
        />
        {showVideoSignInNotice && (
          <DynamicNotification
            type="videoSignIn"
            onClick={onVideoSignInClick}
            onCreate={onVideoCreateNewAccount}
            onTrack={trackVideoSignInNotification}
          />
        )}
        <PasswordInput
          value={password}
          onChange={onPasswordChange}
          label={null}
          experiment={true}
          passwordConfig={passUtils.getDefaultConfig()}
          passwordConfigFetched={true}
          passwordUpdateStatus={null}
        />
        <div className="recaptcha-container">
          <div id="g-recaptcha" />
        </div>
        <Button
          submit
          className="video-signup-input video-signup-CTA"
          size="large"
          disabled={
            !passUtils.meetsAllRequirements(
              password,
              passUtils.getDefaultConfig()
            ) ||
            !email ||
            (isGdprConsentRequired && !isGdprConsentGranted) ||
            !solvedCaptcha
          }
          loading={
            createOrgLoading || checkEmailIsSSOEnabledPending || splitLoading
          }
        >
          Get started
        </Button>
        {isGdprConsentRequired && (
          <Consent
            checked={isGdprConsentGranted}
            onChange={() => setGdprConsentGranted(!isGdprConsentGranted)}
          />
        )}
        {!disabledEmail && !isGdprConsentRequired && (
          <Fragment>
            <div className="touchless-start-oauth-border">
              <p>or</p>
            </div>
            <div className="video-signup-oauth-container">
              <GoogleAuth
                onProcessingGoogleCode={onProcessingGoogleCode}
                onProcessingExternalGoogleCode={onProcessingExternalGoogleCode}
              />
              <MicrosoftAuth
                onProcessingMicrosoftCode={onProcessingMicrosoftCode}
              />
            </div>
            {oauthError && (
              <p className="touchless-start-button-error">{oauthError}</p>
            )}
          </Fragment>
        )}
      </Div>
      <Div className="video-signup-card-footer">
        <span>Already have an account?</span>
        <A href={loginUrl}>Sign in</A>
      </Div>
    </Form>
  )
}

export default SignupCard
